// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImageProps } from "@guestsupply/MobiusOverrides/LazyImage/LazyImage";
import { color, ellipsis, xScreen } from "@guestsupply/XDL/Utilities";
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    heading = "heading",
    // minimumHeight = "minimumHeight",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    bannerWidth = "bannerWidth",
    // imageOverlay = "imageOverlay",
    // partialOverlay = "partialOverlay",
    // partialOverlayPositioning = "partialOverlayPositioning",
    disableButton = "disableButton",
    contentPadding = "contentPadding",
    subHeadingContentElipsis = "subHeadingContentElipsis",
    contentAlignment = "contentAlignment",
    centerTextVertically = "centerTextVertically",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
    // XNG-Change
    mainImage = "mainImage",
    hasBackground = "hasBackground",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        // [fields.minimumHeight]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        // [fields.imageOverlay]: string;
        // [fields.partialOverlay]: boolean;
        // [fields.partialOverlayPositioning]: "top" | "middle" | "bottom";
        [fields.disableButton]: boolean;
        [fields.contentPadding]: number;
        [fields.subHeadingContentElipsis]: boolean;
        [fields.contentAlignment]: "Full" | "Left" | "Right" | "Center";
        [fields.centerTextVertically]: boolean;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.mainImage]: string;
        [fields.hasBackground]: boolean;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
    // XNG-Change
    heading?: TypographyProps;
    subHeading?: TypographyProps;
    contentImage?: LazyImageProps;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: white;
            text-align: center;
        `,
    },
};

const getContentAlignmentStyle = (contentAlignment: "Full" | "Left" | "Right" | "Center") => {
    let contentAlignmentStyle;
    switch (contentAlignment) {
        case "Full":
            contentAlignmentStyle = "";
            break;
        case "Left":
            contentAlignmentStyle = "align-items: flex-start;";
            break;
        case "Right":
            contentAlignmentStyle = "align-items: flex-end;";
            break;
        case "Center":
            contentAlignmentStyle = "align-items: center;";
            break;
    }
    return contentAlignmentStyle;
};

export const Banner: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const backgroundStyles =
        fields.background === "image"
            ? `background-image: url(${parseImageUrl(fields.image).url});
           background-size: cover;`
            : `background-color: ${fields.backgroundColor};`;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    // let minimumHeightStyles;
    // switch (fields.minimumHeight) {
    //     case "1/4 viewport":
    //         minimumHeightStyles = "min-height: 25vh;";
    //         break;
    //     case "1/2 viewport":
    //         minimumHeightStyles = "min-height: 50vh;";
    //         break;
    //     case "3/4 viewport":
    //         minimumHeightStyles = "min-height: 75vh;";
    //         break;
    //     case "fullViewport":
    //         minimumHeightStyles = "min-height: 100vh;";
    //         break;
    // }

    // let overlayPositioningStyles;
    // if (!fields.partialOverlay) {
    //     overlayPositioningStyles = "align-items: stretch;";
    // } else if (fields.partialOverlayPositioning === "top") {
    //     overlayPositioningStyles = "align-items: flex-start;";
    // } else if (fields.partialOverlayPositioning === "middle") {
    //     overlayPositioningStyles = "align-items: center;";
    // } else if (fields.partialOverlayPositioning === "bottom") {
    //     overlayPositioningStyles = "align-items: flex-end;";
    // }

    const styles = useMergeStyles("banner", bannerStyles, extendedStyles);

    let fontSizeStyles;
    if (fields.responsiveFontSizes || fields.customFontSizes) {
        fontSizeStyles = responsiveStyleRules(fields.responsiveFontSizes, fields.customFontSizes ? fields : undefined);
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const wrapperStyles = {
        css: css`
            border-radius: 4px;
            ${styles.wrapper?.css || ""}
            ${fields.hasBackground && backgroundStyles}
            ${focalPointStyles}
            ${fontSizeStyles}
        `,
    };

    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            ${xScreen("gt", "md")} {
                ${fields.mainImage &&
                css`
                    justify-content: space-between;
                `}
            }
        `,
    };

    const slideCenteringWrapperStyles = {
        css: css`
            ${getContentAlignmentStyle(fields.contentAlignment)}
            ${styles.slideCenteringWrapperStyles?.css || ""}
            ${xScreen("gt", "md")} {
                padding: ${fields.contentPadding}px;
                ${fields.mainImage &&
                css`
                    background: linear-gradient(90deg, ${color("common.ui2")} 0%, ${color("common.ui1")} 100%);
                    border-radius: 4px;
                    width: 50%;
                `}
            }
            ${fields.centerTextVertically && `justify-content: center;`}

            ${fields.mainImage &&
            css`
                background: ${color("common.ui2")};
                border-radius: 0 0 4px 4px;
            `}

            ${fields.contentAlignment !== "Full" &&
            css`
                width: 50%;
            `}
        `,
    };

    const subHeading = {
        css: css`
            ${styles.subHeading?.css || ""}
            ${fields.subHeadingContentElipsis &&
            css`
                ${TypographyStyle} {
                    ${xScreen("gt", "md")} {
                        ${ellipsis(3)}
                    }
                }
            `}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="banner-wrapper" {...wrapperStyles}>
                <StyledWrapper className="banner-overlay-wrapper" {...overlayWrapperStyles}>
                    <StyledWrapper className="banner-center-wrapper" {...slideCenteringWrapperStyles}>
                        <Typography {...styles.heading} className="banner-heading">
                            {parse(fields.heading, parserOptions)}
                        </Typography>
                        <Typography {...subHeading} className="banner-subheading">
                            {parse(fields.subheading, parserOptions)}
                        </Typography>
                        {(fields.buttonLabel || title || url) && (
                            <Button
                                disabled={fields.disableButton}
                                {...styles.bannerButton}
                                variant={fields.variant}
                                onClick={onClick}
                                aria-label={fields.buttonLabel || title || url}
                            >
                                {fields.buttonLabel || title || url}
                            </Button>
                        )}
                    </StyledWrapper>
                    {fields.mainImage && (
                        <LazyImage
                            width="656"
                            height="297"
                            src={fields.mainImage}
                            {...styles.contentImage}
                            customTransformations={{ default: "GUEST_Product_600x600" }}
                            altText="banner main image"
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.banner-wrapper {
}

.banner-overlay-wrapper {
}

.banner-center-wrapper {
}

.banner-heading {
}

.banner-subheading {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: Banner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            // XNG-Change
            {
                fieldType: "Contextual",
                name: fields.hasBackground,
                displayName: "Has Background",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
                tab: basicTab,
                isVisible: widget => widget.fields.hasBackground === true,
            },
            {
                fieldType: "Contextual",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.hasBackground === true && widget.fields.background === "image",
                tab: basicTab,
            },
            // {
            //     fieldType: "Contextual",
            //     name: fields.imageOverlay,
            //     displayName: "Image Color Overlay",
            //     editorTemplate: "ColorPickerField",
            //     defaultValue: "",
            //     isVisible: widget => widget.fields.background === "image",
            //     tab: basicTab,
            // },
            // {
            //     fieldType: "Contextual",
            //     name: fields.partialOverlay,
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     tab: basicTab,
            // },
            // {
            //     fieldType: "Contextual",
            //     name: fields.partialOverlayPositioning,
            //     editorTemplate: "DropDownField",
            //     options: [
            //         { displayName: "Top", value: "top" },
            //         { displayName: "Middle", value: "middle" },
            //         { displayName: "Bottom", value: "bottom" },
            //     ],
            //     hideEmptyOption: true,
            //     defaultValue: "bottom",
            //     isVisible: widget => widget.fields.partialOverlay,
            //     tab: basicTab,
            // },
            {
                fieldType: "Contextual",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.hasBackground === true && widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.hasBackground === true && widget.fields.background === "color",
                tab: basicTab,
            },
            // XNG-Change
            {
                fieldType: "Contextual",
                name: fields.mainImage,
                editorTemplate: "ImagePickerField",
                displayName: "Display image next to content",
                defaultValue: "",
                isVisible: widget => widget.fields.hasBackground === false,
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                // XNG-CHANGES : GUEST-2657 : Removing tertiary option
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "Contextual",
                tab: basicTab,
            },
            // {
            //     fieldType: "Contextual",
            //     name: fields.minimumHeight,
            //     displayName: "Minimum Banner Height",
            //     editorTemplate: "DropDownField",
            //     options: [
            //         { displayName: "1/4 Viewport", value: "1/4 viewport" },
            //         { displayName: "1/2 Viewport", value: "1/2 viewport" },
            //         { displayName: "3/4 Viewport", value: "3/4 viewport" },
            //         { displayName: "Full Viewport", value: "fullViewport" },
            //     ],
            //     defaultValue: "1/4 viewport",
            //     hideEmptyOption: true,
            //     tab: basicTab,
            // },
            {
                fieldType: "Contextual",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                fieldType: "Contextual",
                name: fields.contentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: "centerTextVertically",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.subHeadingContentElipsis,
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
            },
            {
                name: fields.contentAlignment,
                displayName: "Content Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "Left" },
                    { displayName: "Right", value: "Right" },
                    { displayName: "Center", value: "Center" },
                ],
                hideEmptyOption: true,
                defaultValue: "Full",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
